import { useMutation } from '@apollo/client';
import { MUTATION } from '../gql-operations';

import useAddMetadata from '../hooks/useAddMetadata';

import customAnalytics from "../util/custom-analytics";

const stripTypename = value => {
    if (Array.isArray(value)) {
        return value.map(stripTypename);
    }

    if (value instanceof Date) {
        return value;
    }

    if (value && typeof value === 'object') {
        return Object.keys(value).reduce((accumulator, key) => {
            if (key === '__typename') {
                return accumulator;
            }

            accumulator[key] = stripTypename(value[key]);
            return accumulator;
        }, {});
    }

    return value;
};

const broadcastMessageUpdate = updatedMessage => {
    if (typeof window === 'undefined') {
        return;
    }

    if (!updatedMessage || !updatedMessage.id) {
        return;
    }

    window.dispatchEvent(
        new CustomEvent('messageUpdated', {
            detail: {
                message: updatedMessage
            }
        })
    );
};

const sanitizeMediaInput = media => {
    if (!media || typeof media !== 'object') {
        return undefined;
    }

    const {
        public_id,
        url,
        name,
        shared_asset_id,
        displayName,
        type,
        format,
        size,
        aspect_ratio,
        width,
        height,
        mime,
        duration,
        ocrText
    } = media;

    if (!url || !type) {
        return undefined;
    }

    const normalizedType = String(type).toUpperCase();

    return {
        public_id,
        url,
        name,
        shared_asset_id,
        displayName,
        type: normalizedType,
        format,
        size: Number(size || 0),
        aspect_ratio,
        width,
        height,
        mime,
        duration,
        ocrText
    };
};

const sanitizeGallerySlideInput = slide => {
    if (!slide || typeof slide !== 'object') {
        return null;
    }

    const {
        type,
        image,
        image_name,
        media_asset_id,
        width,
        height,
        aspect_ratio,
        media_url,
        media_name,
        media_cdn_url,
        media_hls_url,
        media_duration,
        media_has_audio,
        media
    } = slide;

    return {
        type,
        image,
        image_name,
        media_asset_id,
        width,
        height,
        aspect_ratio,
        media_url,
        media_name,
        media_cdn_url,
        media_hls_url,
        media_duration,
        media_has_audio,
        media: sanitizeMediaInput(media)
    };
};

const sanitizeUrlMetaInput = meta => {
    if (!meta || typeof meta !== 'object') {
        return {};
    }

    const {
        author,
        date,
        description,
        lang,
        article_url,
        article_body,
        article_pull_provider,
        image,
        image_asset_id,
        gallery_images,
        gallery,
        media_url,
        media_name,
        media_cdn_url,
        media_type,
        media_width,
        media_height,
        media_duration,
        media_has_audio,
        media_audio_url,
        media_audio_name,
        media_hls_url,
        media_asset_id,
        media_audio_asset_id,
        logo,
        publisher,
        title,
        url,
        public_id,
        image_name,
        name,
        displayName,
        type,
        format,
        size,
        aspect_ratio,
        width,
        embed,
        author_image,
        author_image_public_id,
        author_image_name,
        author_image_size,
        author_image_format,
        author_image_aspect_ratio,
        author_image_width,
        author_image_original,
        quoted_url_meta
    } = meta;

    return {
        author,
        date,
        description,
        lang,
        article_url,
        article_body,
        article_pull_provider,
        image,
        image_asset_id,
        gallery_images: Array.isArray(gallery_images) ? gallery_images : undefined,
        gallery: Array.isArray(gallery) ? gallery.map(sanitizeGallerySlideInput).filter(Boolean) : undefined,
        media_url,
        media_name,
        media_cdn_url,
        media_type,
        media_width,
        media_height,
        media_duration,
        media_has_audio,
        media_audio_url,
        media_audio_name,
        media_hls_url,
        media_asset_id,
        media_audio_asset_id,
        logo,
        publisher,
        title,
        url,
        public_id,
        image_name,
        name,
        displayName,
        type,
        format,
        size,
        aspect_ratio,
        width,
        embed,
        author_image,
        author_image_public_id,
        author_image_name,
        author_image_size,
        author_image_format,
        author_image_aspect_ratio,
        author_image_width,
        author_image_original,
        quoted_url_meta: quoted_url_meta ? sanitizeUrlMetaInput(quoted_url_meta) : undefined
    };
};

function useUpdateMessageMutation() {
    const [updateMessageMutation] = useMutation(MUTATION.UPDATE_MESSAGE, {
        onCompleted: res => {
            customAnalytics.message_updated(res.updateMessage);
        }
    });
    const { updatedFromMeta } = useAddMetadata();

    const mutateMessage = async (messageId, content = {}) => {
        // Skip mutation for temp/optimistic messages - they don't exist on server yet
        if (messageId?.startsWith('temp-')) {
            if (process.env.NODE_ENV === 'development') {
                console.log('⚠️ useUpdateMessage: Skipping update for temp message:', messageId);
            }
            return null;
        }
        
        const sanitizedContent = stripTypename(content);

        const result = await updateMessageMutation({
            variables: {
                data: {
                    messageId,
                    content: {
                        ...sanitizedContent,
                        metadata: {
                            ...(sanitizedContent.metadata || {}),
                            ...updatedFromMeta()
                        }
                    }
                }
            }
        });

        const updatedMessage = result?.data?.updateMessage;
        if (updatedMessage) {
            broadcastMessageUpdate(updatedMessage);
        }

        return updatedMessage;
    };

    const updateURL = async (message, urlMeta) => {
        const cleanList = Array.isArray(urlMeta)
            ? urlMeta.map(sanitizeUrlMetaInput)
            : [];

        return mutateMessage(message.id, { urlMeta: cleanList });
    };

    const updateMessage = async (message, values) => {
        const updatedMessage = await mutateMessage(message.id, {
            title: values.title,
            body: values.body
        });

        if ((!message.body || (message.body && message.body.length === 0)) && values.body.length > 0) {
            customAnalytics.message_edit({
                "action": "Add",
                "type": "Note",
                "note_length": values.body.length
            }, message);
        }

        return updatedMessage;
    };

    const updateFiles = async (message, files) => {
        // Message queries include resolver-only media fields such as thumbnail and
        // cdn_url. Strip those before sending MediaInput back through GraphQL.
        const sanitizedFiles = {
            ...files,
            ...(Object.prototype.hasOwnProperty.call(files, 'file') && {
                file: files.file ? sanitizeMediaInput(files.file) : files.file
            }),
            ...(Object.prototype.hasOwnProperty.call(files, 'image') && {
                image: files.image ? sanitizeMediaInput(files.image) : files.image
            })
        };
        return mutateMessage(message.id, sanitizedFiles);
    };

    const deleteFileMutation = async message => {
        const updatedMessage = await mutateMessage(message.id, { file: null });
        customAnalytics.message_edit({
            "action": "Delete",
            "type": "File"
        }, message);
        return updatedMessage;
    };

    const deleteImageMutation = async message => {
        const updatedMessage = await mutateMessage(message.id, { image: null });
        customAnalytics.message_edit({
            "action": "Delete",
            "type": "Image"
        }, message);
        return updatedMessage;
    };

    return { updateMessage, updateURL, updateFiles, deleteFileMutation, deleteImageMutation };
}

export default useUpdateMessageMutation;
